<template>
  <div
    class="flex h-full w-full flex-col overflow-hidden bg-white"
    :class="
      compact ? 'rounded-[8px]' : 'rounded-[10px] border border-gray-border'
    "
  >
    <div
      v-if="showOpenButton && !compact"
      class="flex shrink-0 justify-end gap-2 border-b border-gray-border bg-white px-3 py-2 sm:px-4"
    >
      <span
        v-if="downloadError"
        class="mr-auto self-center text-[12px] font-helvetica-400 text-orange"
        role="status"
      >
        {{ downloadError }}
      </span>
      <button
        v-if="showDownloadButton"
        type="button"
        class="shrink-0 rounded-[7px] border border-gray-border px-3 py-1.5 text-[13px] font-helvetica-700 text-gray-dark transition hover:border-orange hover:text-orange disabled:cursor-wait disabled:opacity-60"
        :disabled="isDownloading"
        @click.stop="downloadPdf"
      >
        {{ isDownloading ? "Скачивание..." : "Скачать" }}
      </button>
      <a
        :href="src"
        target="_blank"
        rel="noopener noreferrer"
        class="shrink-0 rounded-[7px] border border-gray-border px-3 py-1.5 text-[13px] font-helvetica-700 text-gray-dark transition hover:border-orange hover:text-orange"
        @click.stop
      >
        Открыть
      </a>
    </div>

    <iframe
      v-if="previewFrameUrl"
      :src="previewFrameUrl"
      title="PDF предпросмотр"
      class="min-h-0 flex-1 bg-white"
      :class="compact ? 'pointer-events-none' : ''"
    ></iframe>

    <div
      v-else
      class="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-6 text-center text-gray-dark"
      :class="compact ? 'gap-1 p-2' : ''"
    >
      <span
        class="font-helvetica-700"
        :class="compact ? 'text-[12px]' : 'text-[18px]'"
      >
        {{ errorMessage || "Загрузка PDF..." }}
      </span>
      <span
        v-if="!errorMessage && !compact"
        class="text-[14px] font-helvetica-400 text-extra-text-color"
      >
        Подготавливаем предпросмотр
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useMediaDownload } from "~/composables/useMediaDownload";

const props = withDefaults(
  defineProps<{
    src: string;
    compact?: boolean;
    showOpenButton?: boolean;
    showDownloadButton?: boolean;
  }>(),
  {
    compact: false,
    showOpenButton: true,
    showDownloadButton: true,
  },
);

const previewUrl = ref("");
const errorMessage = ref("");
const { downloadError, downloadMedia, isDownloading, resetDownloadState } =
  useMediaDownload();

const previewFrameUrl = computed(() => {
  if (!previewUrl.value) return "";
  const viewParams = props.compact
    ? "page=1&view=Fit&zoom=page-fit"
    : "page=1&view=FitH&zoom=page-width";
  return `${previewUrl.value}#toolbar=0&navpanes=0&scrollbar=0&${viewParams}`;
});

let objectUrl = "";
let controller: AbortController | null = null;

const downloadPdf = async () => {
  await downloadMedia(props.src);
};

const revokePreviewUrl = () => {
  if (!objectUrl || typeof URL === "undefined") return;
  URL.revokeObjectURL(objectUrl);
  objectUrl = "";
};

const loadPdf = async (src: string) => {
  controller?.abort();
  controller = null;
  revokePreviewUrl();
  previewUrl.value = "";
  errorMessage.value = "";
  resetDownloadState();

  if (!src || typeof window === "undefined") return;

  const nextController = new AbortController();
  controller = nextController;

  try {
    const response = await fetch(src, {
      credentials: "omit",
      signal: nextController.signal,
    });

    if (!response.ok) {
      throw new Error("PDF недоступен для предпросмотра");
    }

    const buffer = await response.arrayBuffer();
    const blob = new Blob([buffer], { type: "application/pdf" });

    if (nextController.signal.aborted) return;

    objectUrl = URL.createObjectURL(blob);
    previewUrl.value = objectUrl;
  } catch (error) {
    if (nextController.signal.aborted) return;
    errorMessage.value =
      error instanceof Error && error.message
        ? error.message
        : "PDF недоступен для предпросмотра";
  } finally {
    if (controller === nextController) {
      controller = null;
    }
  }
};

watch(
  () => props.src,
  (src) => {
    void loadPdf(src);
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  controller?.abort();
  revokePreviewUrl();
});
</script>
